/* ==========================================================================
   DIREVULF — hero-campaign.js

   Two jobs, both small.

   1. THE HEADER SITS ON THE PICTURE. `.dv-header-section` is sticky and in
      the flow, so whatever follows it normally begins below it. The hero
      rises by exactly the header's height, written as a negative top margin
      on the hero's own section wrapper. The section already renders the
      merchant's figures inline, so the page is laid out correctly before this
      runs; this replaces them with the header's real measured height, which
      is what keeps it right when the announcement bar is switched off, when a
      logo changes the bar's height, or at a width nobody thought about.

      Measured, not assumed — and re-measured on resize.

   2. THE CAROUSEL. One transform on one track: translate3d(-n * 100%, 0, 0).
      There is no vertical term anywhere in this file, so a slide change
      cannot move the page up or down, and no control is transformed at all —
      the arrows and the dots are painted where the stylesheet puts them and
      stay there.

   Nothing here touches the header, the announcement bar, the search, the
   microphone or any other carousel in the theme.
   ========================================================================== */
(function () {
  'use strict';

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------- header overlap */

  function headerHeight() {
    /* The sticky wrapper, not the bar inside it: the wrapper is what occupies
       the flow, and its height is what the hero has to climb past. */
    var section = $('.dv-header-section') || ($('[data-dv-header]') && $('[data-dv-header]').closest('.shopify-section'));
    if (!section) return 0;
    var h = Math.round(section.getBoundingClientRect().height);
    return h > 0 ? h : 0;
  }

  function applyOverlap(hero) {
    if (!hero.classList.contains('dv-hero2--overlay')) return;
    var wrapper = hero.closest('.shopify-section') || hero.parentElement;
    if (!wrapper) return;
    /* Only the first section on the page may rise behind the header; anywhere
       else a negative margin would climb over real content. */
    var main = wrapper.parentElement;
    if (main && main.firstElementChild !== wrapper) {
      var before = wrapper.previousElementSibling;
      if (before && !before.classList.contains('dv-header-section') && before.offsetHeight > 0) return;
    }
    var h = headerHeight();
    if (!h) return;
    wrapper.style.marginTop = '-' + h + 'px';
  }

  /* -------------------------------------------------------------- carousel */

  function Hero(root) {
    this.root = root;
    this.track = $('[data-dv-hero-track]', root);
    this.slides = $$('[data-dv-hero-slide]', root);
    this.dots = $$('[data-dv-hero-dot]', root);
    this.index = 0;
    this.timer = null;
    this.delay = (parseFloat(root.getAttribute('data-dv-hero-autoplay')) || 0) * 1000;
    this.paused = false;
    if (!this.track || this.slides.length < 2) { this.slides.length && this.paint(); return; }
    this.bind();
    this.start();
  }

  Hero.prototype.paint = function () {
    /* The only thing that moves is this number, and it only ever feeds
       translateX. */
    this.root.style.setProperty('--dv-hero-i', String(this.index));
    for (var i = 0; i < this.slides.length; i++) {
      var on = i === this.index;
      this.slides[i].classList.toggle('is-on', on);
      if (on) this.slides[i].removeAttribute('aria-hidden');
      else this.slides[i].setAttribute('aria-hidden', 'true');
      /* A link on a slide nobody can see must not be a tab stop. */
      var cta = this.slides[i].querySelector('.dv-hero2__cta');
      if (cta) { if (on) cta.removeAttribute('tabindex'); else cta.setAttribute('tabindex', '-1'); }
    }
    for (var d = 0; d < this.dots.length; d++) {
      var active = d === this.index;
      this.dots[d].classList.toggle('is-active', active);
      this.dots[d].setAttribute('aria-selected', active ? 'true' : 'false');
    }
  };

  Hero.prototype.go = function (next) {
    var n = this.slides.length;
    this.index = ((next % n) + n) % n;
    this.paint();
  };

  Hero.prototype.start = function () {
    this.stop();
    if (!this.delay || reduced || this.slides.length < 2) return;
    var self = this;
    this.timer = window.setInterval(function () {
      if (self.paused || document.hidden) return;
      self.go(self.index + 1);
    }, this.delay);
  };

  Hero.prototype.stop = function () {
    if (this.timer) { window.clearInterval(this.timer); this.timer = null; }
  };

  Hero.prototype.bind = function () {
    var self = this;

    var prev = $('[data-dv-hero-prev]', this.root);
    var next = $('[data-dv-hero-next]', this.root);
    if (prev) prev.addEventListener('click', function () { self.go(self.index - 1); self.start(); });
    if (next) next.addEventListener('click', function () { self.go(self.index + 1); self.start(); });

    this.dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        self.go(parseInt(dot.getAttribute('data-dv-hero-dot'), 10) || 0);
        self.start();
      });
    });

    /* Autoplay is a convenience, not something to fight: it stops while the
       shopper is over the hero or has focus inside it, and while the tab is
       in the background. */
    /* Mouse only: on a touch screen the browser fires a compatibility
       mouseenter on tap and no mouseleave, which used to leave autoplay
       paused for good after the first tap. */
    this.root.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') self.paused = true; });
    this.root.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') self.paused = false; });
    this.root.addEventListener('focusin', function () { self.paused = true; });
    this.root.addEventListener('focusout', function () { self.paused = false; });

    /* Swipe. Horizontal only, and only a real flick counts — a vertical drag
       is the shopper scrolling the page and is left alone. */
    var x0 = 0, y0 = 0, t0 = 0, tracking = false;
    this.root.addEventListener('touchstart', function (event) {
      if (event.touches.length !== 1) { tracking = false; return; }
      tracking = true; x0 = event.touches[0].clientX; y0 = event.touches[0].clientY; t0 = Date.now();
    }, { passive: true });
    this.root.addEventListener('touchend', function (event) {
      if (!tracking) return;
      tracking = false;
      var t = event.changedTouches && event.changedTouches[0];
      if (!t) return;
      var dx = t.clientX - x0, dy = t.clientY - y0;
      if (Math.abs(dx) < 45 || Math.abs(dx) <= Math.abs(dy) || Date.now() - t0 > 800) return;
      self.go(self.index + (dx < 0 ? 1 : -1));
      self.start();
    }, { passive: true });

    /* Arrow keys, but only once the shopper has actually reached the hero. */
    this.root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') { event.preventDefault(); self.go(self.index - 1); self.start(); }
      else if (event.key === 'ArrowRight') { event.preventDefault(); self.go(self.index + 1); self.start(); }
    });
  };

  /* ------------------------------------------------------------------ boot */

  var heroes = [];

  function init(scope) {
    $$('[data-dv-hero]', scope || document).forEach(function (root) {
      if (root.hasAttribute('data-dv-hero-ready')) return;
      root.setAttribute('data-dv-hero-ready', '');
      applyOverlap(root);
      heroes.push(new Hero(root));
    });
  }

  function remeasure() {
    $$('[data-dv-hero]').forEach(applyOverlap);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();

  /* The header's height settles after its own webfonts and logo have loaded,
     so the measurement is taken again once everything is in. */
  window.addEventListener('load', remeasure);
  window.addEventListener('resize', remeasure);
  window.addEventListener('orientationchange', remeasure);

  /* Theme Editor: a section re-rendered by the editor arrives without any of
     this attached. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
    remeasure();
  });
  document.addEventListener('shopify:section:select', function (event) {
    var hero = event.target.querySelector ? event.target.querySelector('[data-dv-hero]') : null;
    if (hero) heroes.forEach(function (h) { if (h.root === hero) h.stop(); });
  });
  document.addEventListener('shopify:section:deselect', function () {
    heroes.forEach(function (h) { h.start(); });
  });
  document.addEventListener('shopify:block:select', function (event) {
    var slide = event.target.closest ? event.target.closest('[data-dv-hero-slide]') : null;
    var root = slide && slide.closest('[data-dv-hero]');
    if (!slide || !root) return;
    heroes.forEach(function (h) {
      if (h.root !== root) return;
      h.stop();
      h.go(parseInt(slide.getAttribute('data-dv-hero-slide'), 10) || 0);
    });
  });
  document.addEventListener('shopify:block:deselect', function () {
    heroes.forEach(function (h) { h.start(); });
  });
})();
